import React, {
  useEffect,
  useRef,
  useState
} from "react";

import ReactCrop, {
  centerCrop,
  makeAspectCrop
} from "react-image-crop";

import "react-image-crop/dist/ReactCrop.css";


const getCenteredCrop = (
  width,
  height,
  aspect
) => {

  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90
      },
      aspect,
      width,
      height
    ),
    width,
    height
  );

};


const cropImageToFile = async (
  image,
  crop,
  originalFile
) => {

  const scaleX =
    image.naturalWidth / image.width;

  const scaleY =
    image.naturalHeight / image.height;

  const canvas =
    document.createElement("canvas");

  canvas.width =
    Math.max(
      1,
      Math.round(crop.width * scaleX)
    );

  canvas.height =
    Math.max(
      1,
      Math.round(crop.height * scaleY)
    );

  const context =
    canvas.getContext("2d");

  if (!context) {
    throw new Error(
      "Unable to create canvas context."
    );
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  context.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const outputType =
    originalFile.type || "image/jpeg";

  const quality =
    outputType === "image/jpeg"
      ? 0.92
      : undefined;

  const blob =
    await new Promise((resolve) => {

      canvas.toBlob(
        resolve,
        outputType,
        quality
      );

    });

  if (!blob) {
    throw new Error(
      "Unable to create cropped image."
    );
  }

  const extensionMatch =
    originalFile.name.match(
      /\.[^/.]+$/
    );

  const extension =
    extensionMatch
      ? extensionMatch[0]
      : ".jpg";

  const baseName =
    originalFile.name.replace(
      /\.[^/.]+$/,
      ""
    );

  return new File(
    [blob],
    `${baseName}-cropped${extension}`,
    {
      type: blob.type,
      lastModified: Date.now()
    }
  );

};


const ImageCropModal = ({
  file,
  aspect,
  title,
  onCancel,
  onApply
}) => {

  const imageRef =
    useRef(null);

  const [crop, setCrop] =
    useState();

  const [completedCrop, setCompletedCrop] =
    useState(null);

  const [imageUrl, setImageUrl] =
    useState("");

  const [croppedFile, setCroppedFile] =
    useState(null);

  const [previewUrl, setPreviewUrl] =
    useState("");

  const [stage, setStage] =
    useState("crop");

  const [applying, setApplying] =
    useState(false);

  const [error, setError] =
    useState("");


  useEffect(() => {

    if (!file) {
      setImageUrl("");
      return;
    }

    const objectUrl =
      URL.createObjectURL(file);

    setImageUrl(objectUrl);

    setCrop(undefined);
    setCompletedCrop(null);

    setCroppedFile(null);
    setPreviewUrl("");

    setStage("crop");
    setError("");

    return () => {
      URL.revokeObjectURL(objectUrl);
    };

  }, [file]);


  useEffect(() => {

    return () => {

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

    };

  }, [previewUrl]);


  const handleImageLoad = (event) => {

    const {
      naturalWidth,
      naturalHeight
    } = event.currentTarget;

    const initialCrop =
      getCenteredCrop(
        naturalWidth,
        naturalHeight,
        aspect
      );

    setCrop(initialCrop);

  };


  const handlePreviewCrop = async () => {

    if (
      !imageRef.current ||
      !completedCrop ||
      completedCrop.width <= 0 ||
      completedCrop.height <= 0
    ) {
      setError(
        "Please select a valid crop area."
      );

      return;
    }

    try {

      setApplying(true);
      setError("");

      const newCroppedFile =
        await cropImageToFile(
          imageRef.current,
          completedCrop,
          file
        );

      const newPreviewUrl =
        URL.createObjectURL(
          newCroppedFile
        );

      setCroppedFile(
        newCroppedFile
      );

      setPreviewUrl(
        newPreviewUrl
      );

      setStage("preview");

    } catch (cropError) {

      console.error(
        "Crop preview failed:",
        cropError
      );

      setError(
        "Unable to create the cropped preview."
      );

    } finally {

      setApplying(false);

    }

  };


  const handleContinueEditing = () => {

    setStage("crop");

    setError("");

  };


  const handleUseCrop = () => {

    if (!croppedFile) {
      return;
    }

    onApply(croppedFile);

  };


  return (
    <div className="admin-image-crop-modal">

      <div className="admin-image-crop-modal-content">

        <div className="admin-image-crop-header">

          <div>

            <h2>
              {stage === "crop"
                ? title || "Crop Image"
                : "Review Cropped Image"}
            </h2>

            <p>
              {stage === "crop"
                ? "Adjust the crop area, then preview the result before applying it."
                : "Review the final crop before using it in your post."}
            </p>

          </div>

        </div>


        {stage === "crop" && (

          <>

            <div className="admin-image-crop-workspace">

              {imageUrl && (

                <ReactCrop
                  crop={crop}
                  aspect={aspect}
                  onChange={(_, percentCrop) =>
                    setCrop(percentCrop)
                  }
                  onComplete={(pixelCrop) =>
                    setCompletedCrop(pixelCrop)
                  }
                  keepSelection
                >

                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="Crop preview"
                    onLoad={handleImageLoad}
                  />

                </ReactCrop>

              )}

            </div>


            {error && (

              <p className="admin-image-crop-error">
                {error}
              </p>

            )}


            <div className="admin-image-crop-actions">

              <button
                type="button"
                onClick={onCancel}
                disabled={applying}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handlePreviewCrop}
                disabled={
                  applying ||
                  !completedCrop
                }
              >
                {applying
                  ? "Preparing Preview..."
                  : "Preview Crop"}
              </button>

            </div>

          </>

        )}


        {stage === "preview" && (

          <>

            <div className="admin-image-crop-preview">

              {previewUrl && (

                <img
                  src={previewUrl}
                  alt="Cropped preview"
                  className="admin-image-crop-preview-image"
                />

              )}

            </div>


            <p className="admin-image-crop-preview-note">
              This is the exact image that will be uploaded.
            </p>


            <div className="admin-image-crop-actions">

              <button
                type="button"
                onClick={onCancel}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleContinueEditing}
              >
                Continue Editing
              </button>

              <button
                type="button"
                onClick={handleUseCrop}
              >
                Apply Crop
              </button>

            </div>

          </>

        )}

      </div>

    </div>
  );

};


export default ImageCropModal;